import { BackButton } from "../../../../../common/components/back_button";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useChecklistServicoObrasItemViewModel } from "../../viewModels/checklist_servico_obras_item_view_model";
import { Box, Breadcrumbs, Link, styled, Tab, Tabs, Typography } from "@mui/material";
import { Link as LinkTo } from 'react-router';
import { HeaderResumo } from "./components/header_resumo";
import { HeaderGrafico } from "./components/header_grafico";
import { BarChart, barClasses, barLabelClasses } from "@mui/x-charts";
import { ItemChecklistServicoModal } from "./components/item_checklist_servico_modal";

import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import dayjs from "dayjs";

/**
 * Table style
 */

const TableContainer = styled('div')({
  '::-webkit-scrollbar': {
    width: '0.35rem',
    height: '0.35rem',
  },
  '::-webkit-scrollbar-thumb': {
    backgroundColor: '#f58634',
  }
});

/**
 * Graph style
 */

const GraphContainer = styled('div')({
  '::-webkit-scrollbar': {
    width: '0.35rem',
    height: '0.35rem',
  },
  '::-webkit-scrollbar-thumb': {
    backgroundColor: '#f58634',
  }
});

/**
 * Calculate cell size
 */

const calculateColumnWidth = (charCount: number, min: number, max: number): string => {
  if (charCount > 250) return `${max}rem`;
  if (charCount === 0) return `${min}rem`;
  return `${min + ((charCount / 250) * (max - min))}rem`;
}

const calculateColumnHeight = (charCount: number, min: number, max: number): string => {
  if (charCount > 250) return `${max}rem`;
  if (charCount === 0) return `${min}rem`;
  return `${min + ((charCount / 250) * (max - min))}rem`;
}

/**
 * ChecklistServicoObrasItem
 */

export const ChecklistServicoObrasItem = () => {
  const viewModel = useChecklistServicoObrasItemViewModel();
  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <BackButton
                onClick={viewModel.handleBack}
              />
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Obras
          </Link>
          <Link underline="hover" color="inherit" component={'span'}>
            <LinkTo to={`/ChecklistServico_lista`}>Checklist de Serviço</LinkTo>
          </Link>
          <Typography className="text-primary-green">
            Checklist Gráfico Obras
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Checklist Gráfico Obras</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex gap-2">
            </div>
          </div>
          {
            viewModel.checklistServico &&
            <div className="w-full flex flex-col gap-0">
              <div className="w-full flex flex-row items-center">
                <span className="text-black dark:text-white font-bold text-lg">
                  {viewModel.checklistServico.codigo} | {viewModel.checklistServico.descricao}
                </span>
              </div>
              {
                (viewModel.checklistServico.revisao && viewModel.checklistServico.revisao.length > 0) &&
                <span className="text-gray-600 dark:text-gray-300 font-bold text-base">
                  Revisão: {viewModel.checklistServico.revisao}
                </span>
              }
            </div>
          }
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    variant="scrollable"
                    scrollButtons="auto"
                    slotProps={{
                      startScrollButtonIcon: {
                        className: "text-black dark:text-white",
                      },
                      endScrollButtonIcon: {
                        className: "text-black dark:text-white",
                      },
                      indicator: {
                        style: {
                          background: '#009C4E',
                        }
                      }
                    }}
                    value={viewModel.currentTab}
                    onChange={viewModel.handleTabChange}>
                    <Tab
                      sx={{
                        textTransform: "none",
                        '&.Mui-selected': {
                          color: '#009C4E',
                        },
                      }}
                      label="Resumo"
                      value={1}
                    />
                    <Tab
                      sx={{
                        textTransform: "none",
                        '&.Mui-selected': {
                          color: '#009C4E',
                        },
                      }}
                      label="Gráfico"
                      value={2}
                    />
                  </Tabs>
                </Box>
                {
                  viewModel.currentTab === 1 &&
                  <>
                    <HeaderResumo
                      descricao={viewModel.filters.resumoDescricao}
                      status={viewModel.filters.status}
                      centroCustos={viewModel.filters.centroCustos}
                      dataAberturaInicio={viewModel.filters.dataAberturaInicio}
                      dataAberturaFinal={viewModel.filters.dataAberturaTermino}
                      dataEncerramentoInicio={viewModel.filters.dataEncerramentoInicio}
                      dataEncerramentoFinal={viewModel.filters.dataEncerramentoTermino}
                      centroCustoDropdown={viewModel.centroCustoDropdown ?? []}
                      onDescricaoChange={viewModel.handleResumoDescricaoChange}
                      onStatusChange={viewModel.handleStatusChange}
                      onDataAberturaChange={viewModel.handleDataAberturaChange}
                      onDataEncerramentoChange={viewModel.handleDataEncerramentoChange}
                      onCentroCustosChange={viewModel.handleGraficoCentroCustosChange}
                      onClearFilters={viewModel.handleClearFilters}
                    />

                    <TableContainer className="w-full min-h-[35rem] max-h-[45rem] overflow-auto bg-white dark:bg-[#2c2c2c] border border-[#90C090] dark:border-[#707070]">
                      {
                        viewModel.estatisticasChecklistServico &&
                        <table className="table-fixed w-full text-black dark:text-white border-collapse border border-[#90C090] dark:border-[#707070]">
                          <thead className="bg-[#f0f0f0] text-black">
                            <tr>
                              <th className="w-[15rem] max-w-[25rem] py-2 border border-[#90C090] dark:border-[#707070]">Descrição Área</th>
                              <th className="w-[10rem] py-2 border border-[#90C090] dark:border-[#707070]">Data da Abertura</th>
                              <th className="w-[10rem] py-2 border border-[#90C090] dark:border-[#707070]">Data de encerramento</th>
                              <th className="w-[5rem] py-2 px-2 border border-[#90C090] dark:border-[#707070]">Total Itens</th>
                              {
                                (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields) &&
                                viewModel.activeFields.map(([fieldKey, _], index) => {
                                  const field = viewModel.estatisticasChecklistServico!.fields[fieldKey];
                                  const descricao = field.descricao ?? field.resumoItem ?? "";
                                  return (
                                    <th
                                      key={index}
                                      className="border border-[#90C090] dark:border-[#707070] px-2 py-2"
                                      style={{
                                        width: calculateColumnWidth(descricao.length, 5, 20),
                                        height: calculateColumnHeight(descricao.length, 5, 20),
                                      }}>
                                      <div className="size-full text-sm flex flex-col justify-center items-center">
                                        {descricao}
                                      </div>
                                    </th>
                                  );
                                })
                              }
                            </tr>
                          </thead>
                          <tbody>
                            {
                              viewModel.estatisticasChecklistServico.data.map(
                                (item) => {
                                  return (
                                    <tr key={item.idInspecaoObra} className="even:bg-[#509050] odd:bg-[#75B075] text-white">
                                      <td className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full px-2 py-1">
                                          {item.areaDescricao}
                                        </div>
                                      </td>
                                      <td className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full px-2 py-1">
                                          {item.dataInspecao ? dayjs(item.dataInspecao).format("DD/MM/YYYY") : ""}
                                        </div>
                                      </td>
                                      <td className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full px-2 py-1">
                                          {item.dataEncerramento ? dayjs(item.dataEncerramento).format("DD/MM/YYYY") : ""}
                                        </div>
                                      </td>
                                      <td className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full px-2 py-1">
                                          {item.totalItens}
                                        </div>
                                      </td>
                                      {
                                        (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields) &&
                                        viewModel.activeFields.map(([fieldKey, _], index) => {
                                          return (
                                            <td key={index} className="border border-[#90C090] dark:border-[#707070]">
                                              <div className="size-full px-2 py-1">
                                                {item.values[fieldKey]}
                                              </div>
                                            </td>
                                          );
                                        })
                                      }
                                    </tr>
                                  );
                                }
                              )
                            }
                          </tbody>
                        </table>
                      }
                    </TableContainer>

                    <TableContainer className="w-full overflow-auto bg-white dark:bg-[#2c2c2c] border border-[#90C090] dark:border-[#707070]">
                      {
                        viewModel.estatisticasChecklistServico &&
                        <>
                          <table className="table-fixed w-full text-black dark:text-white border-collapse border border-[#90C090] dark:border-[#707070]">
                            <thead>
                              <tr className="bg-[#f0f0f0] text-black">
                                <th className="w-[15rem] max-w-[25rem] py-2">STATUS</th>
                                {
                                  (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields) &&
                                  viewModel.activeFields.map(([fieldKey, _], index) => {
                                    const field = viewModel.estatisticasChecklistServico!.fields[fieldKey];
                                    const descricao = field.descricao ?? field.resumoItem ?? "";
                                    return (
                                      <th
                                        key={index}
                                        className="border border-[#90C090] dark:border-[#707070] px-2 py-2"
                                        style={{
                                          width: calculateColumnWidth(descricao.length, 5, 20),
                                          height: calculateColumnHeight(descricao.length, 5, 20),
                                        }}>
                                        <div className="size-full text-sm flex flex-col justify-center items-center">
                                          {descricao}
                                        </div>
                                      </th>
                                    );
                                  })
                                }
                              </tr>
                            </thead>
                            <tbody className="font-bold">
                              <tr className="bg-[#509050] text-white border border-[#90C090] dark:border-[#707070]">
                                <td className="py-2 bg-[#dff0d8] text-black">APROVADOS</td>
                                {
                                  (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields && viewModel.estatisticasChecklistServico.statistics) &&
                                  viewModel.activeFields.map(([fieldKey, _], index) => {
                                    const { statistics } = viewModel.estatisticasChecklistServico!;
                                    return (
                                      <td
                                        key={index}
                                        className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full text-sm px-2">
                                          {fieldKey in statistics ? statistics[fieldKey].aprovados : ''}
                                        </div>
                                      </td>
                                    );
                                  })
                                }
                              </tr>
                              <tr className="bg-[#75B075] text-white border border-[#90C090] dark:border-[#707070]">
                                <td className="py-2 bg-[#f2dede] text-black border border-[#90C090] dark:border-[#707070]">REPROVADOS</td>
                                {
                                  (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields && viewModel.estatisticasChecklistServico.statistics) &&
                                  viewModel.activeFields.map(([fieldKey, _], index) => {
                                    const { statistics } = viewModel.estatisticasChecklistServico!;
                                    return (
                                      <td
                                        key={index}
                                        className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full text-sm px-2">
                                          {fieldKey in statistics ? statistics[fieldKey].reprovados : ''}
                                        </div>
                                      </td>
                                    );
                                  })
                                }
                              </tr>
                              <tr className="bg-[#509050] text-white border border-[#90C090] dark:border-[#707070]">
                                <td className="py-2 bg-[#fcf8e3] text-black">RETRABALHOS</td>
                                {
                                  (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields && viewModel.estatisticasChecklistServico.statistics) &&
                                  viewModel.activeFields.map(([fieldKey, _], index) => {
                                    const { statistics } = viewModel.estatisticasChecklistServico!;
                                    return (
                                      <td
                                        key={index}
                                        className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full text-sm px-2">
                                          {fieldKey in statistics ? statistics[fieldKey].retrabalhos : ''}
                                        </div>
                                      </td>
                                    );
                                  })
                                }
                              </tr>
                              <tr className="bg-[#75B075] text-white border border-[#90C090] dark:border-[#707070]">
                                <td className="py-2 bg-[#d9edf7] text-black">NÃO SE APLICA</td>
                                {
                                  (viewModel.estatisticasChecklistServico && viewModel.estatisticasChecklistServico.fields && viewModel.estatisticasChecklistServico.statistics) &&
                                  viewModel.activeFields.map(([fieldKey, _], index) => {
                                    const { statistics } = viewModel.estatisticasChecklistServico!;
                                    return (
                                      <td
                                        key={index}
                                        className="border border-[#90C090] dark:border-[#707070]">
                                        <div className="size-full text-sm px-2">
                                          {fieldKey in statistics ? statistics[fieldKey].naoSeAplica : ''}
                                        </div>
                                      </td>
                                    );
                                  })
                                }
                              </tr>
                            </tbody>
                          </table>
                        </>
                      }
                    </TableContainer>
                  </>
                }
                {
                  viewModel.currentTab === 2 &&
                  <>
                    <HeaderGrafico
                      status={viewModel.filters.status}
                      centroCustos={viewModel.filters.centroCustos}
                      dataAberturaInicio={viewModel.filters.dataAberturaInicio}
                      dataAberturaFinal={viewModel.filters.dataAberturaTermino}
                      dataEncerramentoInicio={viewModel.filters.dataEncerramentoInicio}
                      dataEncerramentoFinal={viewModel.filters.dataEncerramentoTermino}
                      centroCustoDropdown={viewModel.centroCustoDropdown ?? []}
                      onStatusChange={viewModel.handleStatusChange}
                      onCentroCustosChange={viewModel.handleGraficoCentroCustosChange}
                      onDataAberturaChange={viewModel.handleDataAberturaChange}
                      onDataEncerramentoChange={viewModel.handleDataEncerramentoChange}
                      onClearFilters={viewModel.handleClearFilters}
                    />

                    <GraphContainer className="w-full h-[30rem] mb-16 p-2 overflow-x-auto bg-light-mode-2 dark:bg-dark-mode-2 rounded-[4px] border-[1px] border-border-light-gray dark:border-border-dark-gray">
                      <div style={{
                        height: '100%',
                        width: viewModel.chartWidth !== null ? viewModel.chartWidth : '100%'
                      }}>
                        {viewModel.xAxis &&
                          <BarChart
                            margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                            series={viewModel.series}
                            xAxis={viewModel.xAxis}
                            yAxis={[{ width: 75, min: 0, max: viewModel.maxValue, label: 'Quantidade' }]}
                            width={viewModel.chartWidth !== null ? viewModel.chartWidth : undefined}
                            onAxisClick={(_, item) => viewModel.handleItemChecklistClick(item)}
                            sx={{
                              [`& .${barClasses.root} .${barClasses.seriesLabels} .${barLabelClasses.root}`]: {
                                fill: 'white',
                                fontSize: '14px',
                                paintOrder: 'stroke',
                                stroke: '#000000',
                                strokeWidth: '2px',
                                strokeLinecap: 'butt',
                                strokeLinejoin: 'miter',
                                fontWeight: 800,
                              },
                            }}
                          />
                        }
                      </div>
                    </GraphContainer>
                  </>
                }
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <ItemChecklistServicoModal
        open={viewModel.itemChecklistServicoStatisticModal}
        saving={viewModel.loading}
        loading={viewModel.loading}
        selectedItemChecklistServico={viewModel.selectedItemChecklistServico}
        onClose={viewModel.handleItemChecklistServicoModalOpenState(false)}
      />
    </>
  );
}